/**
 * /onboard/create-household — first-time household creation.
 * AC-013 / AC-014. Single text field + Continue.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/TextInput';
import { FormField } from '../../components/ui/FormField';
import {
  useCreateHousehold,
  useSetActiveHousehold,
} from '../../lib/queries';
import { useAppStore, useToast } from '../../lib/store';

export default function CreateHouseholdRoute() {
  const navigate = useNavigate();
  const [name, setName] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const create = useCreateHousehold();
  const setActive = useSetActiveHousehold();
  const toast = useToast();

  const nameTrimmed = name.trim();
  const isValid = nameTrimmed.length >= 2 && nameTrimmed.length <= 80;
  const showError = touched && !isValid;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    try {
      const hh = await create.mutateAsync({ name: nameTrimmed });
      await setActive.mutateAsync({ household_id: hh.id });
      useAppStore.getState().setActiveHousehold(hh.id);
      toast.success(`Welcome to the ${hh.name} journal.`);
      // First-time editorial welcome step (AC-014). Returning users will
      // skip past this thanks to the `hasSeenWelcome` localStorage gate.
      navigate('/onboarding/welcome');
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "We couldn't create that household.",
      );
    }
  }

  return (
    <section className="mx-auto max-w-md w-full pt-16 pb-12 px-6">
      <header className="mb-10 text-center">
        <p className="text-xs uppercase tracking-[0.18em] text-ink-500 mb-3">
          A new beginning
        </p>
        <h1 className="font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
          Name this household.
        </h1>
        <p className="mt-3 font-sans text-sm text-ink-600 leading-relaxed">
          You can rename it later. We&apos;ll seed it with restaurants, hotels,
          and services to get you started.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-8" noValidate>
        <FormField
          label="Household name"
          htmlFor="cn-onb-name"
          {...(showError
            ? { error: 'Choose a name between 2 and 80 characters.' }
            : {})}
        >
          <TextInput
            id="cn-onb-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="The Hayes Family"
            autoFocus
            maxLength={80}
          />
        </FormField>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={create.isPending || setActive.isPending}
          loadingLabel="Setting up…"
          disabled={!isValid}
        >
          Continue
        </Button>
      </form>
    </section>
  );
}
